import { forwardRef, useState, type InputHTMLAttributes } from "react";
import { Eye, EyeOff } from "lucide-react";

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  erro?: string;
}

/**
 * Campo de formulário padrão do app. Usa só tokens semânticos (bg-surface-2,
 * text-text, border-border...) — se adapta sozinho a claro/escuro em
 * qualquer fundo (Modal, painel de config, ou os cards de Login/Cadastro),
 * conforme a regra de temas nativos da Sprint 3. Até a rodada da marca Elos
 * este componente tinha um prop `tema="claro"|"escuro"` com cores fixas
 * (branco sempre) para uso sobre o antigo painel verde do Login — removido
 * junto com o redesign dessas telas, que passaram a usar os mesmos tokens.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, erro, id, className = "", type, ...props }, ref) => {
    const inputId = id ?? props.name;
    const ehCampoSenha = type === "password";

    const [senhaVisivel, setSenhaVisivel] = useState(false);
    const tipoReal = ehCampoSenha ? (senhaVisivel ? "text" : "password") : type;

    return (
      <div className="flex flex-col gap-1.5">
        <label htmlFor={inputId} className="text-sm font-medium tracking-wide text-text">
          {label}
        </label>
        <div className="relative">
          <input
            ref={ref}
            id={inputId}
            type={tipoReal}
            // F2-03 (revisão 20/07/2026): campo desabilitado (ex.: CPF em edição)
            // não tinha NENHUMA diferença visual do campo editável — parecia
            // travado sem explicação. Agora fica em cinza com cursor bloqueado.
            className={`w-full rounded-md border bg-surface-2 px-4 py-2.5 text-sm text-text placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-primary disabled:cursor-not-allowed disabled:bg-border/40 disabled:text-muted disabled:opacity-80 ${erro ? "border-danger" : "border-transparent"} ${ehCampoSenha ? "pr-11" : ""} ${className}`}
            aria-invalid={!!erro}
            aria-describedby={erro ? `${inputId}-erro` : undefined}
            {...props}
          />
          {ehCampoSenha && (
            <button
              type="button"
              onClick={() => setSenhaVisivel((v) => !v)}
              tabIndex={-1}
              aria-label={senhaVisivel ? "Ocultar senha" : "Mostrar senha"}
              className="absolute inset-y-0 right-0 flex items-center px-3 text-muted transition-colors hover:text-text"
            >
              {senhaVisivel ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          )}
        </div>
        {erro && (
          <span id={`${inputId}-erro`} className="text-xs text-danger">
            {erro}
          </span>
        )}
      </div>
    );
  },
);

Input.displayName = "Input";
